import * as React from "react";

import i18n from "@client-shared/lib/i18n";
import * as api from "@client-shared/services/api";
import { handleDisplayErrorData } from "../../services/api/utilities/handleDisplayErrorData";
import { useIsGetAuthRoute } from "../auth/SFDCAuthProvider";
import { useSFDCAuthState } from "../auth/useSFDCAuthState";
import useVendorContext from "../vendor/useCurrentVendor";
import { initialState, reducer, setLoading, setServices } from "./actions";
import { ServicesContextState } from "./types";

const defaultContextState: ServicesContextState = [initialState, () => undefined];

export const ServicesContext = React.createContext(defaultContextState);

type Props = {
  children: React.ReactNode;
};

export const ServicesProvider: React.FC<Props> = ({ children }: Props) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const isAuthRoute = useIsGetAuthRoute();
  const [{ sfdcIdToken, isRoasterAdmin }] = useSFDCAuthState();
  const [{ currentVendor }] = useVendorContext();
  const previousVendorKeyRef = React.useRef(currentVendor?.vendorKey);

  // Reset loading when vendor changes so services are re-fetched for the new vendor (e.g. after admin impersonation)
  React.useEffect(() => {
    const currentVendorKey = currentVendor?.vendorKey;
    if (previousVendorKeyRef.current !== currentVendorKey) {
      dispatch(setLoading(true));
    }
    previousVendorKeyRef.current = currentVendorKey;
  }, [currentVendor?.vendorKey]);

  React.useEffect(() => {
    if (isAuthRoute) {
      return;
    }
    const fetchServices = async () => {
      try {
        const services = await api.getServices("remote");
        dispatch(setServices(services));
        dispatch(setLoading(false));
      } catch (e) {
        console.log("Error in fetching services", e);
        handleDisplayErrorData(i18n.t("apiErrorMessages.getServices.message"), i18n.t("apiErrorMessages.getServices.title"), e as Error);
      } finally {
        dispatch(setLoading(false));
      }
    };
    // Roaster admin users have no vendorKey until they impersonate a vendor.
    // The getServices endpoint requires a vendorKey header for admin users (Lambda authorizer enforces this).
    // Skip fetching until a vendor is selected to avoid a 403 → token refresh loop → logout.
    const hasVendorForRequest = !isRoasterAdmin || Boolean(currentVendor?.vendorKey);
    if (state.loading && sfdcIdToken && hasVendorForRequest) {
      void fetchServices();
    }
  }, [state.loading, isAuthRoute, sfdcIdToken, isRoasterAdmin, currentVendor?.vendorKey]);

  const contextValue: ServicesContextState = React.useMemo(() => [state, dispatch], [state, dispatch]);
  return <ServicesContext.Provider value={contextValue}>{children}</ServicesContext.Provider>;
};
